import { type RefObject, useCallback, useEffect, useRef, useState } from 'react'

const POLL_INTERVAL_MS = 500
/** 再生中に先読みがこの秒数を切ったら、途切れ途切れに再生する前に止めて貯める */
const BUFFER_LOW_SEC = 3
/** 再開に必要な先読みの下限・上限（秒） */
const MIN_RESUME_SEC = 4
const MAX_RESUME_SEC = 20
/** 読込速度が不明なときの再開の目安（秒） */
const DEFAULT_RESUME_SEC = 6
/** 読込が再生に追いつかないとき、再開してから次に止まるまで続けて見られるようにしたい秒数 */
const MIN_CONTINUOUS_PLAY_SEC = 30
/** 読込速度を求める区間（ms） */
const RATE_WINDOW_MS = 4000
/** 止めている間に先読みがこの時間増えなければ、貯まらないとみなしてブラウザに任せる（ms） */
const HOLD_STALL_TIMEOUT_MS = 8000
/** 上記でブラウザに任せたあと、再び止めるまで空ける時間（ms） */
const HOLD_COOLDOWN_MS = 15000
/** 再開した play() が拒否されたかを確認するまでの時間（ms） */
const RESUME_CHECK_MS = 1000
/** 末尾まで読み込み済みとみなす誤差（秒） */
const END_MARGIN_SEC = 0.5

type RateSample = { at: number; position: number; bufferedEnd: number }

/** 再生位置から途切れずに読み込めている範囲の終端 */
export const getBufferedEnd = (video: HTMLVideoElement): number => {
  const position = video.currentTime
  const { buffered } = video
  for (let i = 0; i < buffered.length; i++) {
    if (buffered.start(i) <= position + 0.5 && position < buffered.end(i)) return buffered.end(i)
  }
  return position
}

/**
 * 再開に必要な先読み秒数。
 * 読込が再生より速ければ再生中も先読みが増えるので少しで足り、遅いほど多く貯めて止まる回数を減らす
 */
export const getResumeTargetSec = (downloadRate: number | null, playbackRate: number): number => {
  const speed = Math.max(1, playbackRate)
  if (downloadRate === null) return DEFAULT_RESUME_SEC * speed
  const target = BUFFER_LOW_SEC * speed + MIN_CONTINUOUS_PLAY_SEC * (speed - downloadRate)
  return Math.min(MAX_RESUME_SEC * speed, Math.max(MIN_RESUME_SEC * speed, target))
}

const isBufferedToEnd = (video: HTMLVideoElement, bufferedEnd: number): boolean =>
  video.duration > 0 && bufferedEnd >= video.duration - END_MARGIN_SEC

type Options = {
  videoRef: RefObject<HTMLVideoElement | null>
  /** ユーザーが再生を望んでいるか */
  playing: boolean
  playbackRate: number
  /** 再開の play() が拒否されたとき */
  onResumeFailed: () => void
}

/**
 * 先読みが尽きかけたら一時停止して貯め、十分貯まってから再開する。
 * 回線が遅いと iOS はわずかな先読みで再開と停止を繰り返し、映像と音声がカクつくため
 */
export const useBufferHold = ({ videoRef, playing, playbackRate, onResumeFailed }: Options) => {
  const [isHolding, setIsHolding] = useState(false)
  const holdingRef = useRef(false)
  const playingRef = useRef(playing)
  const cooldownUntilRef = useRef(0)
  const lastGrowthAtRef = useRef(0)
  const lastBufferedEndRef = useRef(0)
  const samplesRef = useRef<RateSample[]>([])
  const startedSrcRef = useRef<string | null>(null)
  const onResumeFailedRef = useRef(onResumeFailed)

  useEffect(() => {
    onResumeFailedRef.current = onResumeFailed
    playingRef.current = playing
  }, [onResumeFailed, playing])

  /** 直近の区間で 1 秒あたりに読み込めた動画の秒数。計測区間が足りない間は null */
  const getDownloadRate = useCallback((): number | null => {
    const samples = samplesRef.current
    const first = samples.at(0)
    const last = samples.at(-1)
    if (!first || !last) return null
    const span = (last.at - first.at) / 1000
    if (span < 1) return null
    return Math.max(0, (last.bufferedEnd - first.bufferedEnd) / span)
  }, [])

  const startHold = useCallback((video: HTMLVideoElement, bufferedEnd: number) => {
    holdingRef.current = true
    lastBufferedEndRef.current = bufferedEnd
    lastGrowthAtRef.current = performance.now()
    setIsHolding(true)
    video.pause()
  }, [])

  /** 読込が再生に追いついていないと分かっているか（追いついていれば先読みが少なくても途切れない） */
  const isFallingBehind = useCallback(
    (playbackRate: number): boolean => {
      const rate = getDownloadRate()
      return rate !== null && rate < Math.max(1, playbackRate)
    },
    [getDownloadRate],
  )

  /**
   * 途中で尽きてブラウザが再開した時点で、読込が追いつかず先読みも足りなければ、そのまま止めて貯める。
   * ブラウザはわずかな先読みで再開するため、これが無いと再開直後にまた止まる
   */
  const holdIfShortOnPlaying = useCallback(() => {
    const video = videoRef.current
    if (!video || holdingRef.current || !playingRef.current) return
    // 開始時はブラウザが再生できると判断した時点ですぐ再生する
    if (startedSrcRef.current !== video.currentSrc) {
      startedSrcRef.current = video.currentSrc
      return
    }
    if (performance.now() < cooldownUntilRef.current) return
    if (!isFallingBehind(video.playbackRate)) return
    const bufferedEnd = getBufferedEnd(video)
    if (isBufferedToEnd(video, bufferedEnd)) return
    const target = getResumeTargetSec(getDownloadRate(), video.playbackRate)
    if (bufferedEnd - video.currentTime >= target) return
    startHold(video, bufferedEnd)
  }, [getDownloadRate, isFallingBehind, startHold, videoRef])

  useEffect(() => {
    if (!playing) return

    let resumeCheckTimer: ReturnType<typeof setTimeout> | undefined
    const speed = Math.max(1, playbackRate)

    const release = (video: HTMLVideoElement, withCooldown: boolean) => {
      holdingRef.current = false
      setIsHolding(false)
      if (withCooldown) cooldownUntilRef.current = performance.now() + HOLD_COOLDOWN_MS
      clearTimeout(resumeCheckTimer)
      resumeCheckTimer = setTimeout(() => {
        if (video.paused && !holdingRef.current) onResumeFailedRef.current()
      }, RESUME_CHECK_MS)
    }

    const recordSample = (sample: RateSample) => {
      const last = samplesRef.current.at(-1)
      const elapsedSec = last ? (sample.at - last.at) / 1000 : 0
      // シークで読み込み範囲が変わったら計測をやり直す
      const jumped =
        last !== undefined &&
        (sample.bufferedEnd < last.bufferedEnd || Math.abs(sample.position - last.position) > elapsedSec * speed + 1)
      const kept = jumped ? [] : samplesRef.current.filter((s) => sample.at - s.at <= RATE_WINDOW_MS)
      samplesRef.current = [...kept, sample]
    }

    const tick = () => {
      const video = videoRef.current
      if (!video || video.readyState < HTMLMediaElement.HAVE_METADATA) return

      const now = performance.now()
      const bufferedEnd = getBufferedEnd(video)
      const ahead = bufferedEnd - video.currentTime
      const bufferedToEnd = isBufferedToEnd(video, bufferedEnd)
      recordSample({ at: now, bufferedEnd, position: video.currentTime })

      if (holdingRef.current) {
        if (bufferedEnd > lastBufferedEndRef.current) {
          lastBufferedEndRef.current = bufferedEnd
          lastGrowthAtRef.current = now
        }
        if (ahead >= getResumeTargetSec(getDownloadRate(), playbackRate) || bufferedToEnd) {
          release(video, false)
          return
        }
        if (now - lastGrowthAtRef.current > HOLD_STALL_TIMEOUT_MS) release(video, true)
        return
      }

      if (now < cooldownUntilRef.current) return
      // 一度も再生していない要素は止めると読み込みも始まらないため対象外
      if (video.played.length === 0 || video.seeking || video.ended) return
      if (bufferedToEnd || ahead >= BUFFER_LOW_SEC * speed) return
      if (!isFallingBehind(playbackRate)) return

      startHold(video, bufferedEnd)
    }

    const timer = setInterval(tick, POLL_INTERVAL_MS)
    return () => {
      clearInterval(timer)
      clearTimeout(resumeCheckTimer)
      // ユーザーが止めたときは貯め待ちも解除する
      holdingRef.current = false
      setIsHolding(false)
    }
  }, [playing, playbackRate, videoRef, startHold, getDownloadRate, isFallingBehind])

  return { isHolding, holdingRef, holdIfShortOnPlaying, getDownloadRate }
}
